import type { Component, Directive, DirectiveBinding, VNode, ComponentPublicInstance } from 'vue'
import { h } from 'vue'
import HoverTip from './tip.vue'
import AmountVue from '@/components/basic-components/dialog/template-mount'
import { addScrollRefresh, debounce, DebounceHandler, isEllipsis, requestAnimationDebounce } from '@/utils/tools'
import { TipConfig, TipModifiers } from './type'
import { getArrowType, getPositionModifier, getTipPositionStyle } from './position'

type TipShowFn = {
  (e?: MouseEvent): void;
  clear(): void;
}

type TipCloseFn = {
  (): void;
  clear(): void;
}

type TipCache = {
  config: TipConfig
  modifiers: TipModifiers
  tipShowFn: TipShowFn
  tipClose: TipCloseFn
  tipSwitch: TipShowFn
  tipEl: HTMLElement
  tipInstance?: ComponentPublicInstance
}

const tipNodes: WeakMap<HTMLElement, TipCache> = new WeakMap()
let currentNode: HTMLElement | null = null
let openingNode: HTMLElement | null = null
let currentTipOpen: TipShowFn | null
let currentTipClose: DebounceHandler<any> | null

function shotEl(el: HTMLElement | null): TipCache | null {
  if (!el) return null
  if (tipNodes.has(el)) {
    return tipNodes.get(el) as TipCache
  } else {
    return shotEl(el.parentElement)
  }
}

function closeTipByEl(el: HTMLElement) {
  const close = tipNodes.get(el)?.tipClose
  if(close) {
    currentTipClose = close
    currentTipClose()
  }
}

const openCb = requestAnimationDebounce((e: MouseEvent) => {
  const el = e.target as HTMLElement
  const {tipClose, tipShowFn, tipSwitch, tipEl} = shotEl(el) || {}
  const needClose = currentNode && currentNode !== tipEl
  const openTip = !currentNode ? tipShowFn : tipSwitch
  const isOpened = currentNode === tipEl
  const isOpening = openingNode === tipEl

  if (isOpening) return

  if (isOpened) {
    if(tipClose === currentTipClose) {
      currentTipClose.clear();
      currentTipClose = null;
    }
    if (currentTipOpen && openingNode !== tipEl) {
      currentTipOpen.clear();
      openingNode = null;
      currentTipOpen = null;
    }
    return;
  }

  if (needClose) {
    closeTipByEl(currentNode!)
  }
  if (openTip) {
    openingNode = tipEl!;
    openTip(e)
    currentTipOpen = openTip
  }
})

window.addEventListener('mousemove', openCb, true)

window.addEventListener('hover', openCb, true)

addScrollRefresh(() => {
  if (currentNode) {
    const handleFn = tipNodes.get(currentNode)?.tipShowFn
    handleFn?.()
  }
})

function getWarpComponent(slot: any): Component {
  return {
    setup() {
      return () => slot
    }
  }
}

/**
 * @description: 鼠标hover时展示一个小tip (Vue 3 版本)
 */
export const hoverTip: Directive<HTMLElement, string | TipConfig> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | TipConfig>, vNode: VNode) {
    const { value, modifiers: originModifiers } = binding

    let originConfig: TipConfig = {}
    if (typeof value === 'object') {
      originConfig = { ...value } as TipConfig
    } else {
      originConfig.text = value
    }

    const delay = originConfig.delay || (originModifiers as TipModifiers).noDelay ? 100 : 800
    const switchDelay = originConfig.switchDelay || (originModifiers as TipModifiers).noDelay ? 100 : 300
    const closeDelay = originConfig.closeDelay || 50

    let tipInstance: ComponentPublicInstance | null = null

    const delayShow = debounce(delay, showTip);
    const delayClose = debounce(closeDelay, closeTip);
    const delaySwitch = debounce(switchDelay, showTip);

    tipNodes.set(el, {
      config: originConfig,
      modifiers: originModifiers as TipModifiers,
      tipShowFn: delayShow,
      tipClose: delayClose,
      tipSwitch: delaySwitch,
      tipEl: el,
    })

    // tip的展示模版
    function getTemplate() {
      let config = tipNodes.get(el)!.config
      let tipTemplate = config.component || HoverTip

      // 通过slot传入自定义组件 (Vue 3 使用 slots)
      const children = vNode.children as any
      const customSlotTip = children?.hoverTip
      const defaultTipSlot = children?.hoverDefaultTip

      // 自定义整体tip
      if (customSlotTip) {
        tipTemplate = getWarpComponent(customSlotTip)
      }

      // 自定义tip文案部分
      if (defaultTipSlot) {
        tipTemplate = {
          setup(props: any) {
            return () => h(HoverTip, props, { default: () => defaultTipSlot })
          }
        }
      }
      return tipTemplate
    }

    function closeTip() {
      if (tipInstance) {
        // Vue 3: 使用 $el 的父节点来 unmount
        const app = (tipInstance as any).$.appContext.app
        if (app && app.unmount) {
          app.unmount()
        }
        tipInstance = null
      }
      if (currentTipClose === closeTip) {
        currentTipClose = null
      }

      if (currentNode === el) {
        currentNode = null
      }
    }

    /**
     * @description: 执行自定义判断条件
     */
    function handleShowFn(config: TipConfig) {
      if (typeof config.showFn === 'function') {
        return config.showFn(el, config)
      }
      return true
    }

    /**
     * @description: 展示tip
     */
    function showTip(e?: MouseEvent, force: boolean = false) {
      if (openingNode !== el) return;
      const currentInfo = tipNodes.get(el)
      if (!currentInfo) return
      const { config, modifiers } = currentInfo
      if (config.disabled) return
      // 展示元素隐藏了
      if (!el.offsetWidth) return
      // 是否满足自定义展示判断
      if (!handleShowFn(config)) return
      // 必须内容超出时才展示
      const isOver = config.over || modifiers.over
      if (isOver && !isEllipsis(el)) return

      let { props, on, nativeOn, style, text } = config
      const tipComponent = getTemplate()
      const hoverTipHandle = AmountVue(tipComponent, {
        props,
        on,
        nativeOn
      })
      const positionTargetNode = config.selector ? el.querySelector(config.selector) || el : el
      const positionModifier = getPositionModifier(config, modifiers)
      const offsetStyle = getTipPositionStyle({
        positionType: positionModifier,
        mouseEvent: e,
        rect: positionTargetNode.getBoundingClientRect(),
        config: {
          offsetX: config.offsetX,
          offsetY: config.offsetY,
          topSet: config.topSet,
          leftSet: config.leftSet
        }
      })
      tipInstance = hoverTipHandle({
        props: {
          value: text,
          arrowPosition: getArrowType(positionModifier),
          noArrow: !positionModifier,
          tipStyle: style || {}
        },
        style: {
          position: 'absolute',
          ...offsetStyle,
          pointerEvents: modifiers.onlyEl ? 'none' : ''
        }
      })
      const cache = tipNodes.get(el)
      cache!.tipInstance = tipInstance

      // 将 tipInstance.$el 作为 key
      const tipEl = (tipInstance as any).$el as HTMLElement
      if (tipEl) {
        tipNodes.set(tipEl, cache!)
      }

      currentNode = el
      openingNode = null
      currentTipOpen = null

      if (typeof config.showCallback === 'function') {
        config.showCallback()
      }
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding<string | TipConfig>) {
    const { value } = binding
    let text = ''
    const cache = tipNodes.get(el)
    if (typeof value === 'object') {
      text = (value as TipConfig).text || ''
    } else {
      text = value
    }
    if (cache) {
      cache.config.text = text
    }
    if (cache?.tipInstance) {
      // Vue 3: 使用 $props 更新
      const instance = cache.tipInstance as any
      if (instance.$props) {
        instance.$props.value = text
      }
    }
  },

  unmounted(el: HTMLElement) {
    const cache = tipNodes.get(el)
    if (cache?.tipInstance) {
      const app = (cache.tipInstance as any).$.appContext.app
      if (app && app.unmount) {
        app.unmount()
      }
    }
    tipNodes.delete(el)
    if (currentNode === el) {
      currentNode = null
    }
    if (openingNode === el) {
      openingNode = null
    }
  }
}
